import { t } from '@lingui/core/macro';

import TooltipRow from '@/components/Flare/guides/TooltipRow';
import MultiSeriesTooltip from '@/components/MultiSeriesTooltip/MultiSeriesTooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { numberFormat } from '@/helper/numberFormatter';

type ViewThroughPercentCellRow = {
  label: string;
  color: string;
  viewThroughVisits: number;
};

type Props = {
  rows: ViewThroughPercentCellRow[];
  tooltipTitle: string;
  viewThroughPercent: number;
};

const getNonNegativeValue = (value: number) => Math.max(value, 0);

const ViewThroughPercentCell = ({ rows, tooltipTitle, viewThroughPercent }: Props) => {
  const rowsData = rows.map((row) => ({
    ...row,
    viewThroughVisits: getNonNegativeValue(row.viewThroughVisits),
  }));

  const totalViewThroughVisits = rowsData.reduce((sum, row) => sum + row.viewThroughVisits, 0);
  const viewThroughPercentText =
    viewThroughPercent > 0
      ? numberFormat(viewThroughPercent, {
          isPercent: true,
          precision: 1,
        })
      : '0%';

  if (totalViewThroughVisits === 0) {
    return viewThroughPercentText;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<span className="cursor-help">{viewThroughPercentText}</span>} />
      <TooltipContent side="bottom" className="max-w-150 p-5">
        <MultiSeriesTooltip title={tooltipTitle}>
          {rowsData.map((row) => (
            <TooltipRow
              key={row.label}
              color={row.color}
              name={row.label}
              value={t`${numberFormat(row.viewThroughVisits)} View Throughs`}
              secondaryValue={
                row.viewThroughVisits > 0 &&
                t`(${numberFormat(row.viewThroughVisits / totalViewThroughVisits, {
                  isPercent: true,
                  precision: 1,
                })})`
              }
            />
          ))}
          <TooltipRow
            name={t`All`}
            value={t`${numberFormat(totalViewThroughVisits)} View Throughs`}
          />
        </MultiSeriesTooltip>
      </TooltipContent>
    </Tooltip>
  );
};

export type { ViewThroughPercentCellRow };
export default ViewThroughPercentCell;
